import { Component, inject, signal, model, OnDestroy, input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CommonService } from '../../../services/common.service';
import { CatalogLccaUnitResponse } from '../../../models/common/lcca_unit.model';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'app-lcca-unit-autocomplete',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteModule
    ],
    templateUrl: './lcca-unit-autocomplete.component.html',
    styleUrl: './lcca-unit-autocomplete.component.scss'
})
export class LccaUnitAutocompleteComponent implements OnDestroy {
    private commonService = inject(CommonService);
    private destroy$ = new Subject<void>();

    // Two-way binding for the value (returns id as number)
    value = model<number | string>('');

    // Input properties
    placeholder = input<string>('Search unit...');
    minLength = 0;
    disabled = input<boolean>(false);

    // Internal state
    filteredUnits = signal<CatalogLccaUnitResponse[]>([]);
    displayValue = signal<string>('');
    private lastFetchedId: number | null = null;

    constructor() {
        // Watch for value changes and fetch name when ID is set
        effect(() => {
            const currentValue = this.value();
            if (typeof currentValue === 'number' && currentValue > 0 && currentValue !== this.lastFetchedId) {
                this.lastFetchedId = currentValue;
                this.fetchNameById(currentValue);
            } else if (currentValue === '' || currentValue === null || currentValue === undefined) {
                this.displayValue.set('');
                this.lastFetchedId = null;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private fetchNameById(id: number): void {
        this.commonService.getLccaUnitById(id)
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => of(null))
            )
            .subscribe((result) => {
                if (result && (result as any).name) {
                    this.displayValue.set((result as any).name);
                }
            });
    }

    // Search units for autocomplete
    searchUnits(event: { query: string }): void {
        const query = event.query || '';

        // Allow empty query if minLength is 0
        if (query.length < this.minLength) {
            this.filteredUnits.set([]);
            return;
        }

        this.commonService.findLccaUnits(1, 50, query)
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => {
                    this.filteredUnits.set([]);
                    return of({ data: [], total: 0, page: 1 });
                })
            )
            .subscribe((result) => {
                this.filteredUnits.set(result.data || []);
            });
    }

    // Handle model change - update display value when user is typing
    onModelChange(value: any): void {
        if (typeof value === 'string') {
            this.displayValue.set(value);
        }
    }

    // Handle selection - returns the id instead of name
    onSelect(event: { value: CatalogLccaUnitResponse }): void {
        const item = event.value;
        const itemId = (item as any).id;
        const itemName = (item as any).name || '';
        this.value.set(itemId);
        this.displayValue.set(itemName);
        this.lastFetchedId = itemId;
    }
}
